/*
 * ProductCardCarousel - Swipeable image carousel for product cards
 * Works on mobile (touch swipe) and desktop (drag + hover dots)
 */
if (!window.customElements.get('product-card-carousel')) {
  window.customElements.define('product-card-carousel', class extends HTMLElement {
    connectedCallback() {
      this._track = this.querySelector('.product-card__carousel-track');
      this._dots = Array.from(this.querySelectorAll('.product-card__carousel-dot'));
      this._slides = Array.from(this.querySelectorAll('.product-card__carousel-slide'));
      this._currentIndex = 0;
      this._startX = 0;
      this._startY = 0;
      this._isDragging = false;
      this._hasMoved = false;
      this._startScrollLeft = 0;

      if (this._slides.length < 2) return;

      // Touch events for mobile swipe
      this._track.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
      this._track.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });
      this._track.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: true });

      // Mouse drag for desktop
      this._track.addEventListener('mousedown', this._onMouseDown.bind(this));
      this._track.addEventListener('mousemove', this._onMouseMove.bind(this));
      this._track.addEventListener('mouseup', this._onMouseUp.bind(this));
      this._track.addEventListener('mouseleave', this._onMouseUp.bind(this));

      // Scroll-based index detection (covers all navigation)
      this._track.addEventListener('scroll', this._onScroll.bind(this), { passive: true });

      // Dot clicks
      this._dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._goTo(parseInt(dot.dataset.index));
        });
      });

      // Prevent link click after drag
      this._track.addEventListener('click', (e) => {
        if (this._hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    _onTouchStart(e) {
      this._startX = e.touches[0].clientX;
      this._startY = e.touches[0].clientY;
      this._hasMoved = false;
      this._isScrollDecided = false;
      this.classList.add('is-swiped');
    }

    _onTouchMove(e) {
      if (!this._isScrollDecided) {
        const dx = Math.abs(e.touches[0].clientX - this._startX);
        const dy = Math.abs(e.touches[0].clientY - this._startY);
        if (dx > 5 || dy > 5) {
          this._isScrollDecided = true;
          this._isHorizontal = dx > dy;
        }
        return;
      }

      if (this._isHorizontal) {
        e.preventDefault();
        this._hasMoved = true;
      }
    }

    _onTouchEnd(e) {
      if (!this._hasMoved) return;

      const endX = e.changedTouches[0].clientX;
      const diff = this._startX - endX;
      const threshold = this._track.offsetWidth * 0.2;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this._currentIndex < this._slides.length - 1) {
          this._goTo(this._currentIndex + 1);
        } else if (diff < 0 && this._currentIndex > 0) {
          this._goTo(this._currentIndex - 1);
        } else {
          this._goTo(this._currentIndex);
        }
      } else {
        this._goTo(this._currentIndex);
      }
    }

    _onMouseDown(e) {
      this._isDragging = true;
      this._hasMoved = false;
      this._startX = e.clientX;
      this._startScrollLeft = this._track.scrollLeft;
      this._track.style.scrollBehavior = 'auto';
      this._track.style.cursor = 'grabbing';
      e.preventDefault();
    }

    _onMouseMove(e) {
      if (!this._isDragging) return;
      const dx = e.clientX - this._startX;
      if (Math.abs(dx) > 3) {
        this._hasMoved = true;
        this.classList.add('is-dragging');
      }
      this._track.scrollLeft = this._startScrollLeft - dx;
    }

    _onMouseUp() {
      if (!this._isDragging) return;
      this._isDragging = false;
      this._track.style.scrollBehavior = 'smooth';
      this._track.style.cursor = '';
      this.classList.remove('is-dragging');

      if (this._hasMoved) {
        // Snap to nearest slide
        const slideWidth = this._slides[0].offsetWidth;
        const nearestIndex = Math.round(this._track.scrollLeft / slideWidth);
        this._goTo(Math.max(0, Math.min(nearestIndex, this._slides.length - 1)));
      }
    }

    _onScroll() {
      if (this._scrollTimer) cancelAnimationFrame(this._scrollTimer);
      this._scrollTimer = requestAnimationFrame(() => {
        const slideWidth = this._slides[0].offsetWidth;
        if (slideWidth === 0) return;
        const newIndex = Math.round(this._track.scrollLeft / slideWidth);
        if (newIndex !== this._currentIndex && newIndex >= 0 && newIndex < this._slides.length) {
          this._currentIndex = newIndex;
          this._updateDots();
        }
      });
    }

    _goTo(index) {
      this._currentIndex = index;
      const slideWidth = this._slides[0].offsetWidth;
      this._track.style.scrollBehavior = 'smooth';
      this._track.scrollLeft = slideWidth * index;
      this._updateDots();
    }

    _updateDots() {
      this._dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this._currentIndex);
      });
    }
  });
}
